import { useEffect, useState } from 'react';
import { InitializationState } from '../types/initialization.types';

interface SplashScreenProps {
  state: InitializationState;
  onRetry?: () => void;
}

/**
 * SplashScreen Component
 *
 * Displays initialization progress with animated loading indicators
 * Shows current task, progress bar, and handles errors gracefully
 */
export function SplashScreen({ state, onRetry }: SplashScreenProps) {
  const [dots, setDots] = useState('');

  // Animate loading dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Get phase message
  const getPhaseMessage = (): string => {
    switch (state.phase) {
      case 'phase1':
        return 'Cargando datos esenciales';
      case 'phase2':
        return 'Preparando tu espacio de trabajo';
      case 'phase3':
        return 'Finalizando configuración';
      case 'complete':
        return '¡Listo!';
      case 'error':
        return 'Ocurrió un error';
      default:
        return 'Iniciando';
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center z-50">
      <div className="max-w-md w-full px-6">
        {/* Logo/Icon */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            {/* Spinning ring */}
            <div className="w-20 h-20 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
            {/* Center icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <svg
                className="w-10 h-10 text-purple-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Phase Message */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {getPhaseMessage()}
          </h2>
          {state.currentTask && (
            <p className="text-sm text-gray-600">
              {state.currentTask}
              {dots}
            </p>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-600 to-blue-600 transition-all duration-500 ease-out"
              style={{ width: `${state.progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">
            {Math.round(state.progress)}%
          </p>
        </div>

        {/* Error State */}
        {state.hasError && state.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-red-600 mt-0.5 mr-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800 mb-1">
                  Error al cargar datos
                </h3>
                <p className="text-xs text-red-700">{state.error.message}</p>
                {!state.error.isCritical && (
                  <p className="text-xs text-red-600 mt-2">
                    Algunos datos no pudieron cargarse, pero puedes continuar.
                  </p>
                )}
              </div>
            </div>

            {/* Retry Button */}
            {state.error.canRetry && onRetry && (
              <button
                onClick={onRetry}
                className="mt-3 w-full px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Reintentar
              </button>
            )}

            {/* Continue Button (for non-critical errors) */}
            {!state.error.isCritical && (
              <button
                onClick={() => window.location.reload()}
                className="mt-2 w-full px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
              >
                Continuar de todos modos
              </button>
            )}
          </div>
        )}

        {/* Completed Tasks */}
        {state.completedTasks.length > 0 && !state.hasError && (
          <div className="text-center">
            <p className="text-xs text-gray-500">
              {state.completedTasks.length} de {state.completedTasks.length + state.failedTasks.length} tareas completadas
            </p>
          </div>
        )}

        {/* Success Animation */}
        {state.isComplete && (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-green-600 animate-bounce"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-sm font-medium text-green-600">
              ¡Todo listo! Redirigiendo...
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
